// src/pages/NewCarDetailPage.tsx

import { useParams } from 'react-router-dom';
import {
  Box,
  Typography,
  Grid,
  Chip,
  Stack,
  Tabs,
  Tab,
  Paper,
  CircularProgress,
  Button,
} from '@mui/material';
import Header from '../components/Header';
import Footer from '../components/Footer';
import NewVehicleImageGallery from '../components/NewVehicleImageGallery';
import NewVehicleVariantsPricing from '../components/NewVehicleVariantsPricing';
import NewVehicleKeyFeatures from '../components/NewVehicleKeyFeatures';
import NewVehicleDealers from '../components/NewVehicleDealers';
import NewVehicleRightSidebar from '../components/NewVehicleRightSidebar';
import { useState, useEffect } from 'react';

// Icons for the Left Column Details & Header Actions
import FavoriteBorderIcon from '@mui/icons-material/FavoriteBorder';
import ShareIcon from '@mui/icons-material/Share';
import MoreHorizIcon from '@mui/icons-material/MoreHoriz';
import EventIcon from '@mui/icons-material/Event';
import SettingsSuggestIcon from '@mui/icons-material/SettingsSuggest';
import DirectionsCarIcon from '@mui/icons-material/DirectionsCar';
import AirlineSeatReclineNormalIcon from '@mui/icons-material/AirlineSeatReclineNormal';

import axiosInstance from '../services/axiosInstance';
import { endpoints } from '../services/endpoints';
import { decodeId } from '../utils/idEncoder';
import NotFoundPage from './NotFoundPage';

import { NewVehicle, VehicleVariant } from '../types/NewVehicleTypes';
import NewVehicleTabPricing from '../components/NewVehicleTabPricing';
import NewVehicleTabSpecs from '../components/NewVehicleTabSpecs';
import NewVehicleFeaturesTab from '../components/NewVehicleFeaturesTab';
import NewVehiclePhotosTab from '../components/NewVehiclePhotosTab';

interface HomePageProps {
  mode: 'light' | 'dark';
  setMode: (mode: 'light' | 'dark') => void;
}

// ─── Component ────────────────────────────────────────────────────────────────

const NewCarDetailPage = ({ mode, setMode }: HomePageProps) => {
  const { id: hashId } = useParams<{ id: string }>();
  const realVehicleId = hashId ? decodeId(hashId) : null;

  const [vehicle, setVehicle] = useState<NewVehicle | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');

  // Controls which variant's data is shown in Pricing/Specs/Features
  const [selectedVariantIndex, setSelectedVariantIndex] = useState(0);

  const [activeTab, setActiveTab] = useState(0);

  const fetchVehicle = async () => {
    if (!realVehicleId) {
      setError('Invalid vehicle ID.');
      setLoading(false);
      return;
    }
    try {
      setLoading(true);
      const res = await axiosInstance.get<{ responseData: NewVehicle }>(
        endpoints.newVehicle.getSinglePublished(realVehicleId.toString())
      );
      setVehicle(res.data?.responseData);
    } catch {
      setError('Failed to fetch vehicle data.');
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchVehicle();
  }, [hashId, realVehicleId]);

  const selectedVariant: VehicleVariant | undefined =
    vehicle?.variantsInfo?.[selectedVariantIndex];

  const adaptedForGallery = {
    vehicleImage: (vehicle?.images ?? []).map((img) => ({ vehicleImageUrl: img.url })),
    vehicleVideo: (vehicle?.videos ?? []).map((vid) => ({ vehicleVideoUrl: vid.url })),
  };

  const currentFeatures = selectedVariant?.featureGroup?.features || [];

  if (error) return <NotFoundPage />;

  return (
    <>
      <Header />
      <Box p={1} display="flex" justifyContent="center" mt={{ xs: 8, md: 12 }}>
        {loading ? (
          <Paper elevation={2} sx={{ p: 2, maxWidth: 1300, width: '100%', borderRadius: 2, display: 'flex', justifyContent: 'center', alignItems: 'center', minHeight: 300 }}>
            <CircularProgress />
          </Paper>
        ) : vehicle && selectedVariant ? (
          <Box sx={{ maxWidth: 1280, width: '100%' }}>

            {/* HEADER AREA */}
            <Box display="flex" flexDirection={{ xs: 'column', md: 'row' }} justifyContent="space-between" alignItems={{ xs: 'flex-start', md: 'center' }} mb={3} gap={2}>
              <Box>
                <Box display="flex" alignItems="center" gap={1.5} flexWrap="wrap">
                  <Typography variant="h4" fontWeight={800}>
                    {selectedVariant.brand} {selectedVariant.model} {selectedVariant.variantName}
                  </Typography>
                  <Chip label="New Vehicle" size="small" sx={{ bgcolor: '#FFD700', color: '#000', fontWeight: 700, borderRadius: 1 }} />
                </Box>
                {vehicle.subtitle && (
                  <Typography variant="body1" color="text.secondary" mt={0.5}>
                    {vehicle.subtitle}
                  </Typography>
                )}
              </Box>

              <Stack direction="row" spacing={1.5}>
                <Button variant="outlined" startIcon={<FavoriteBorderIcon />} color="inherit" sx={{ borderRadius: 2, textTransform: 'none', fontWeight: 600 }}>Shortlist</Button>
                <Button variant="outlined" startIcon={<ShareIcon />} color="inherit" sx={{ borderRadius: 2, textTransform: 'none', fontWeight: 600 }}>Share</Button>
                <Button variant="outlined" color="inherit" sx={{ borderRadius: 2, minWidth: 'auto', px: 1 }}><MoreHorizIcon /></Button>
              </Stack>
            </Box>

            {/* TABS NAVIGATION */}
            <Box sx={{ borderBottom: 1, borderColor: 'divider', mb: 3 }}>
              <Tabs value={activeTab} onChange={(_e, v) => setActiveTab(v)} variant="scrollable" scrollButtons="auto" textColor="inherit" indicatorColor="primary">
                <Tab label="Overview" sx={{ fontWeight: activeTab === 0 ? 700 : 500, color: activeTab === 0 ? '#FFB800' : 'inherit' }} />
                <Tab label="Pricing" />
                <Tab label="Specs" />
                <Tab label="Features" />
                <Tab label="Photos" />
              </Tabs>
            </Box>

            <Grid container spacing={4}>

              {/* MAIN COLUMN (Full width on all tabs except Overview, which has a sidebar) */}
              <Grid size={{ xs: 12, md: activeTab === 0 ? 7 : 12 }}>

                {/* TAB 0: OVERVIEW (Existing Layout) */}
                {activeTab === 0 && (
                  <>
                    <Box sx={{ mb: 4 }}>
                      <NewVehicleImageGallery vehicle={adaptedForGallery} />
                    </Box>

                    <Box mb={4}>
                      <Typography variant="h6" fontWeight={800} mb={1.5}>Overview</Typography>
                      <Typography variant="body1" color="text.secondary" sx={{ lineHeight: 1.7, mb: 3 }}>
                        {vehicle.shortSummary} The all-new {selectedVariant.brand} {selectedVariant.model} {selectedVariant.variantName} is built for reliability, durability, and efficiency.
                      </Typography>

                      <Grid container spacing={2}>
                        <Grid size={{ xs: 12, sm: 6, md: 3 }}>
                          <Paper variant="outlined" sx={{ p: 2, borderRadius: 2, textAlign: 'center', height: '100%' }}>
                            <AirlineSeatReclineNormalIcon sx={{ fontSize: 32, color: '#FFB800', mb: 1 }} />
                            <Typography variant="subtitle2" fontWeight={700}>Spacious Cabin</Typography>
                          </Paper>
                        </Grid>
                        <Grid size={{ xs: 12, sm: 6, md: 3 }}>
                          <Paper variant="outlined" sx={{ p: 2, borderRadius: 2, textAlign: 'center', height: '100%' }}>
                            <SettingsSuggestIcon sx={{ fontSize: 32, color: '#FFB800', mb: 1 }} />
                            <Typography variant="subtitle2" fontWeight={700}>Efficient</Typography>
                          </Paper>
                        </Grid>
                        <Grid size={{ xs: 12, sm: 6, md: 3 }}>
                          <Paper variant="outlined" sx={{ p: 2, borderRadius: 2, textAlign: 'center', height: '100%' }}>
                            <DirectionsCarIcon sx={{ fontSize: 32, color: '#FFB800', mb: 1 }} />
                            <Typography variant="subtitle2" fontWeight={700}>Automatic</Typography>
                          </Paper>
                        </Grid>
                        <Grid size={{ xs: 12, sm: 6, md: 3 }}>
                          <Paper variant="outlined" sx={{ p: 2, borderRadius: 2, textAlign: 'center', height: '100%' }}>
                            <EventIcon sx={{ fontSize: 32, color: '#FFB800', mb: 1 }} />
                            <Typography variant="subtitle2" fontWeight={700}>Reliable</Typography>
                          </Paper>
                        </Grid>
                      </Grid>
                    </Box>

                    <NewVehicleVariantsPricing variants={vehicle.variantsInfo} />
                    <NewVehicleKeyFeatures features={currentFeatures} />
                    <NewVehicleDealers vehicle={vehicle} currentPrice={selectedVariant.priceGroup.currentPrice} />
                  </>
                )}

                {/* PRICING, SPECS, FEATURES (With Side Selection) */}
                {(activeTab === 1 || activeTab === 2 || activeTab === 3) && (
                  <Grid container spacing={4}>
                    {/* LEFT SIDE: Variant Selector (Side Navigation) */}
                    <Grid size={{ xs: 12, md: 3 }}>
                      <Box sx={{ mb: 4, mt: 3 }}>
                        <Typography sx={{ color: 'text.secondary', mb: 1.5, fontSize: '0.9rem', fontWeight: 500 }}>
                          Click on Model to view specifications
                        </Typography>
                        <Stack spacing={0.5}>
                          {vehicle.variantsInfo.map((v, idx) => {
                            const isSelected = selectedVariantIndex === idx;
                            return (
                              <Box
                                key={v.vehicleVariantId}
                                onClick={() => setSelectedVariantIndex(idx)}
                                sx={{
                                  display: 'flex',
                                  alignItems: 'center',
                                  p: 1,
                                  px: 1.5,
                                  borderRadius: 1,
                                  cursor: 'pointer',
                                  bgcolor: isSelected ? '#e5e5e5' : 'transparent',
                                  transition: 'background-color 0.2s',
                                  '&:hover': { bgcolor: isSelected ? '#e5e5e5' : '#f3f4f6' }
                                }}
                              >
                                {/* Checkbox Outline Box */}
                                <Box
                                  sx={{
                                    width: 18,
                                    height: 18,
                                    borderRadius: '4px',
                                    border: '1px solid #666',
                                    mr: 1.5,
                                    bgcolor: '#fff',
                                    flexShrink: 0
                                  }}
                                />

                                {/* Active State Red Arrow */}
                                {isSelected && (
                                  <Typography sx={{ color: 'primary.main', fontSize: '0.75rem', mr: 0.5, lineHeight: 1 }}>
                                    ▶
                                  </Typography>
                                )}

                                <Typography
                                  sx={{
                                    color: isSelected ? 'primary.main' : 'text.secondary',
                                    fontWeight: isSelected ? 500 : 400,
                                    fontSize: '0.95rem'
                                  }}
                                >
                                  {v.variantName}
                                </Typography>
                              </Box>
                            );
                          })}
                        </Stack>
                      </Box>
                    </Grid>

                    {/* RIGHT SIDE: Tab Content */}
                    <Grid size={{ xs: 12, md: 9 }}>
                      {activeTab === 1 && (
                        <NewVehicleTabPricing
                          variants={vehicle.variantsInfo}
                          selectedVariantIndex={selectedVariantIndex}
                        />
                      )}

                      {activeTab === 2 && (
                        <NewVehicleTabSpecs variant={selectedVariant} />
                      )}

                      {activeTab === 3 && (
                        <NewVehicleFeaturesTab variant={selectedVariant} />
                      )}
                    </Grid>
                  </Grid>
                )}

                {/* TAB 4: PHOTOS (Full Width Layout - No Selection) */}
                {activeTab === 4 && (
                  <Box sx={{ mt: 3 }}>
                    <NewVehiclePhotosTab images={vehicle.images} />
                  </Box>
                )}

              </Grid>

              {/* RIGHT SIDEBAR (Only visible on Overview tab) */}
              {activeTab === 0 && (
                <Grid size={{ xs: 12, md: 5 }}>
                  <NewVehicleRightSidebar vehicle={vehicle} selectedVariant={selectedVariant} />
                </Grid>
              )}

            </Grid>

          </Box>
        ) : null}
      </Box>
      <Footer />
    </>
  );
};

export default NewCarDetailPage;